import React, { useState, useEffect, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import { Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { PlayArrow, Pause, Replay } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import GraphService_DN from '../../services/GraphService_DN';
import { getYears } from '../../interfaces/traffic_accidents/FormControllerData';
import SelectionData from '../../interfaces/Graphs/SelectionData';
import { MdLocalPolice } from "react-icons/md";
import './GraphStyles.css';

interface MonthlyData {
    month: number;
    year: number;
    region: string;
    count: number;
}

interface AnimatedMonthlyChartProps {
    isVisible: boolean;
}

export default function AnimatedMonthlyChart({ isVisible }: AnimatedMonthlyChartProps) {
    const { t } = useTranslation("traffic");
    const [data, setData] = useState<MonthlyData[]>([]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(0);
    const [chartData, setChartData] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [selectedYear, setSelectedYear] = useState<string>('2024');
    const [years] = useState<SelectionData[]>([
        { Code: 'all', Type: '\u00A0\u00A0\u00A0\u00A0\u00A0' },
        ...getYears().filter(year => year.Code !== '2016')
    ]);

    const months = [
        t('Traffic.months.january'),
        t('Traffic.months.february'),
        t('Traffic.months.march'),
        t('Traffic.months.april'),
        t('Traffic.months.may'),
        t('Traffic.months.june'),
        t('Traffic.months.july'),
        t('Traffic.months.august'),
        t('Traffic.months.september'),
        t('Traffic.months.october'),
        t('Traffic.months.november'),
        t('Traffic.months.december')
    ];

    // Fetch data for animated chart
    const fetchMonthlyData = useCallback(async () => {
        setLoading(true);
        try {
            const apiUrl = `/Graph/monthly-animals/${selectedYear}`;
            
            const response = await GraphService_DN.FetchDataFrom_API(apiUrl);
            
            if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                // Transform API data to our format
                const transformedData: MonthlyData[] = response.data.map((item: any) => ({
                    month: item.month,
                    year: item.year,
                    region: item.region,
                    count: item.count
                }));
                
                setData(transformedData);
            } else {
                // No data available
                setData([]);
            }
            
        } catch (error) {
            console.error('Error fetching monthly data:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [selectedYear]);

    // Calculate cumulative data up to current month
    const calculateChartData = useCallback((monthIndex: number) => {
        if (!data.length) return {};

        console.log(`Calculating chart data for month ${monthIndex + 1}`);
        console.log('Available data:', data);
        
        const cumulativeData: { [region: string]: number } = {};
        
        // Initialize ALL regions from data with 0, not just hardcoded ones
        const uniqueRegions = Array.from(new Set(data.map(item => item.region)));
        console.log('Unique regions from data:', uniqueRegions);
        
        uniqueRegions.forEach(region => {
            cumulativeData[region] = 0;
        });

        console.log('Initialized regions:', Object.keys(cumulativeData));

        // Calculate cumulative counts up to current month
        for (let m = 1; m <= monthIndex + 1; m++) {
            data.filter(item => item.month === m).forEach(item => {
                cumulativeData[item.region] += item.count;
            });
        }

        console.log('Cumulative data:', cumulativeData);

        // Sort regions by count (descending)
        const sortedRegions = Object.entries(cumulativeData)
            .sort(([, a], [, b]) => b - a)
            .map(([region, count], index) => ({
                name: region,
                value: count,
                rank: index + 1
            }));

        return {
            yAxisData: sortedRegions.map(item => item.name),
            seriesData: sortedRegions.map(item => item.value),
            maxValue: Math.max(...sortedRegions.map(item => item.value))
        };
    }, [data]);

    // Animation logic
    useEffect(() => {
        if (!isPlaying || !data.length) return;

        const interval = setInterval(() => {
            setCurrentMonth(prev => {
                if (prev >= 11) {
                    setIsPlaying(false);
                    return prev;
                }
                return prev + 1;
            });
        }, 1000); // 1 second per month

        return () => clearInterval(interval);
    }, [isPlaying, data]);

    // Update chart data when current month changes
    useEffect(() => {
        const newChartData = calculateChartData(currentMonth);
        setChartData(newChartData);
    }, [currentMonth, calculateChartData]);

    // Fetch data when component becomes visible or year changes
    useEffect(() => {
        if (isVisible) {
            fetchMonthlyData();
        }
    }, [isVisible, selectedYear, fetchMonthlyData]);

    const handlePlay = () => {
        setIsPlaying(true);
    };

    const handlePause = () => {
        setIsPlaying(false);
    };

    const handleRestart = () => {
        setCurrentMonth(0);
        setIsPlaying(true);
    };

    const handleYearChange = (event: any) => {
        setSelectedYear(event.target.value);
        setCurrentMonth(0);
        setIsPlaying(false);
        setData([]);
    };

    const getChartOption = () => {
        if (!chartData.yAxisData) return {};

        return {
            title: [
                {
                    text: t('Traffic.chartTitleAnimals'),
                    left: 'center',
                    top: '2%',
                    textStyle: {
                        fontSize: 22,
                        fontWeight: 'bold'
                    }
                },
                {
                    text: selectedYear === 'all'
                        ? (currentMonth === 0 ? `${months[currentMonth]}` : `${months[0]} - ${months[currentMonth]}`)
                        : (currentMonth === 0 ? `${months[currentMonth]}  ${selectedYear}` : `${months[0]} - ${months[currentMonth]}  ${selectedYear}`),

                    left: 'center',
                    top: '8%',
                    textStyle: {
                        fontSize: 16,
                        fontWeight: 'normal'
                    }
                }
            ],
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                },
                formatter: (params: any) => {
                    const data = params[0];
                    return `${data.name}<br/>${t('Traffic.tooltipAccidents')}: ${data.value}`;
                }
            },
            toolbox: {
                show: true,
                orient: "vertical",
                left: "right",
                top: "center",
                feature: {
                    mark: { show: true },
                    saveAsImage: { show: true },
                },
            },
            grid: {
                left: '15%',
                right: '10%',
                bottom: '10%',
                top: '15%'
            },
            xAxis: {
                type: 'value',
                max: chartData.maxValue ? chartData.maxValue + 10 : 100,
                axisLabel: {
                    formatter: '{value}'
                },
                name: t('Traffic.animatedChart.xAxisLabel'),
                nameLocation: 'middle',
                nameGap: 30,
                nameTextStyle: {
                    fontSize: 14,
                    fontWeight: 'bold'
                }
            },
            yAxis: {
                type: 'category',
                data: chartData.yAxisData || [],
                inverse: true, // Highest values at top
                axisLabel: {
                    interval: 0,
                    fontSize: 12
                }
            },
            series: [
                {
                    name: 'Počet nehôd',
                    type: 'bar',
                    data: chartData.seriesData || [],
                    itemStyle: {
                        color: '#ff6b35',
                        borderRadius: [0, 4, 4, 0]
                    },
                    label: {
                        show: true,
                        position: 'right',
                        formatter: '{c}',
                        fontSize: 12,
                        fontWeight: 'bold',
                        color: '#333'
                    },
                    animationDuration: 800,
                    animationEasing: 'cubicOut'
                }
            ],
            animation: true,
            animationDuration: 800,
            animationEasing: 'cubicOut'
        };
    };

    if (!isVisible) return null;

    return (
        <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-start', 
                alignItems: 'center', 
                marginBottom: '20px' 
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                            <InputLabel>{t('Traffic.year')}</InputLabel>
                            <Select
                                value={selectedYear}
                                onChange={handleYearChange}
                                label={t('Traffic.year')}
                            >
                                {years.map((year) => (
                                    <MenuItem key={year.Code} value={year.Code}>
                                        {year.Type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                    <Button
                        variant="contained"
                        onClick={handlePlay}
                        disabled={isPlaying || loading}
                        size="small"
                        sx={{ 
                            minWidth: '36px', 
                            width: '36px', 
                            height: '36px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <PlayArrow fontSize="small" />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handlePause}
                        disabled={!isPlaying}
                        size="small"
                        sx={{ 
                            minWidth: '36px', 
                            width: '36px', 
                            height: '36px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <Pause fontSize="small" />
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleRestart}
                        disabled={loading}
                        size="small"
                        sx={{ 
                            minWidth: '36px', 
                            width: '36px', 
                            height: '36px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <Replay fontSize="small" />
                    </Button>
                    </div>
                </div>
            </div>

            <div className="graph_wrapper">
                { loading && <div className="loading">
                    <div><MdLocalPolice /> {t('Traffic.loading')}</div>
                </div> }
            
                <ReactECharts 
                    option={getChartOption()}
                    style={{ height: '500px', width: '100%' }}
                    notMerge={true}
                />
            </div>
        </div>
    );
}